import { useMemo, useState } from 'react'
import { Card } from '../../../components/ui/Card'
import { useClientPagination } from '../../../hooks/useClientPagination'
import { Pagination } from '../../../components/ui/Pagination'
import type { EfsaIngredient, Nivel } from './types'
import { NIVEL_DOTS } from './types'

const GRUPE = [
  'Vitamine',
  'Minerale',
  'Acizi grasi',
  'Aminoacizi',
  'Fibre',
  'Extracte plante',
  'Altele',
]

interface Props {
  ingrediente: EfsaIngredient[]
}

export function EfsaTab({ ingrediente }: Props) {
  const [search, setSearch] = useState('')
  const [grupa, setGrupa] = useState<string>('')
  const [nivel, setNivel] = useState<Nivel | ''>('')

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return ingrediente.filter((i) => {
      if (grupa && i.grupa !== grupa) return false
      if (nivel && i.nivel !== nivel) return false
      if (q) {
        const m = i.label.toLowerCase().includes(q) || i.key.toLowerCase().includes(q)
        if (!m) return false
      }
      return true
    })
  }, [ingrediente, search, grupa, nivel])

  const pag = useClientPagination(filtered, 50)

  const counts = useMemo(() => {
    const c: Record<string, { verde: number; portocaliu: number; rosu: number; total: number }> = {}
    for (const i of ingrediente) {
      const g = c[i.grupa] ?? { verde: 0, portocaliu: 0, rosu: 0, total: 0 }
      g[i.nivel]++
      g.total++
      c[i.grupa] = g
    }
    return c
  }, [ingrediente])

  return (
    <div className="space-y-4">
      <Card description="Baza de date EFSA cu cele 85 de ingrediente uzuale în suplimente alimentare. Verde = mențiune de sănătate autorizată. Portocaliu = în așteptare (botanice) sau fără mențiune aprobată. Roșu = mențiune respinsă / risc juridic.">
        <div className="mb-4 grid grid-cols-2 gap-2 text-xs md:grid-cols-7">
          {GRUPE.map((g) => {
            const c = counts[g] ?? { verde: 0, portocaliu: 0, rosu: 0, total: 0 }
            return (
              <button
                key={g}
                onClick={() => setGrupa(grupa === g ? '' : g)}
                className={`rounded border p-2 text-left transition ${
                  grupa === g
                    ? 'border-b4l-blue-dark bg-b4l-blue-light/40'
                    : 'border-b4l-border hover:bg-b4l-bg-soft'
                }`}
              >
                <div className="font-semibold">{g}</div>
                <div className="mt-1 text-[10px] text-b4l-text-muted">
                  {c.total} total · <span className="text-b4l-green-dark">{c.verde}</span> ·{' '}
                  <span className="text-b4l-amber-dark">{c.portocaliu}</span> ·{' '}
                  <span className="text-b4l-red-dark">{c.rosu}</span>
                </div>
              </button>
            )
          })}
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <input
            type="search"
            placeholder="Caută după nume sau cod…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[200px] rounded border border-b4l-border px-3 py-1.5 text-sm"
          />
          <select
            value={nivel}
            onChange={(e) => setNivel(e.target.value as Nivel | '')}
            className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
          >
            <option value="">Toate nivelele</option>
            <option value="verde">Verde (autorizate)</option>
            <option value="portocaliu">Portocaliu (în așteptare / fără mențiune aprobată)</option>
            <option value="rosu">Roșu (respinse)</option>
          </select>
          {(grupa || nivel || search) && (
            <button
              onClick={() => {
                setGrupa('')
                setNivel('')
                setSearch('')
              }}
              className="text-xs text-b4l-text-muted hover:text-b4l-text"
            >
              Resetează filtrele
            </button>
          )}
          <span className="ml-auto text-xs text-b4l-text-muted">
            {filtered.length} / {ingrediente.length}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-2 pr-3 font-medium">Nivel</th>
                <th className="py-2 pr-3 font-medium">Ingredient</th>
                <th className="py-2 pr-3 font-medium">Grupă</th>
                <th className="py-2 pr-3 font-medium">Reg</th>
                <th className="py-2 pr-3 font-medium">Mențiune EFSA</th>
                <th className="py-2 pr-3 font-medium text-right">UL</th>
              </tr>
            </thead>
            <tbody>
              {pag.paginated.map((i) => (
                <tr key={i.id} className="border-b border-b4l-border last:border-0 align-top">
                  <td className="py-2 pr-3">
                    <span
                      className={`inline-block h-2.5 w-2.5 rounded-full ${NIVEL_DOTS[i.nivel]}`}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <div className="font-medium">{i.label}</div>
                    <div className="font-mono text-[10px] text-b4l-text-muted">{i.key}</div>
                  </td>
                  <td className="py-2 pr-3 text-xs">{i.grupa}</td>
                  <td className="py-2 pr-3 text-xs text-b4l-text-muted">{i.reg}</td>
                  <td className="py-2 pr-3 text-xs">
                    {i.mentiune ? (
                      <span>{i.mentiune}</span>
                    ) : (
                      <span className="italic text-b4l-text-muted">{i.conditii ?? '—'}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono text-xs">
                    {i.ul ? `${i.ul} ${i.ulUnit ?? ''}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3">
          <Pagination
            page={pag.page}
            pageSize={pag.pageSize}
            total={pag.total}
            onPageChange={pag.setPage}
            onPageSizeChange={pag.setPageSize}
            itemLabel="ingrediente"
          />
        </div>
      </Card>
    </div>
  )
}
